import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { api, formatApiError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Sun, Moon, Gauge, FileCheck, Lock } from "lucide-react";

export default function Settings() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [templates, setTemplates] = useState(null);
  const [tplBusy, setTplBusy] = useState(false);
  const [priceCfg, setPriceCfg] = useState(null);
  const [priceInput, setPriceInput] = useState("");
  const [priceBusy, setPriceBusy] = useState(false);
  const initials = (user?.name || user?.email || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();

  useEffect(() => {
    api.get("/settings/score-config").then(({ data }) => setCfg(data)).catch(() => {});
    api.get("/settings/doc-templates").then(({ data }) => setTemplates(data.templates)).catch(() => {});
    if (user?.role === "admin") {
      api.get("/admin/unlock-price").then(({ data }) => {
        setPriceCfg(data);
        setPriceInput(String(data.effective.amount));
      }).catch(() => {});
    }
  }, [user?.role]);

  const isAdmin = user?.role === "admin";
  const totalW = cfg ? Object.values(cfg.weights).reduce((a, b) => a + Number(b || 0), 0) : 0;

  const saveCfg = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/settings/score-config", {
        weights: Object.fromEntries(Object.entries(cfg.weights).map(([k, v]) => [k, Number(v)])),
        target_margin_pct: Number(cfg.target_margin_pct),
        dossier_points: Object.fromEntries(Object.entries(cfg.dossier_points).map(([k, v]) => [k, Number(v)])),
      });
      setCfg(data); toast.success("Configurazione salvata");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      <header>
        <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight">Impostazioni</h1>
        <p className="text-muted-foreground text-sm mt-1">Gestisci il tuo account e le preferenze.</p>
      </header>

      <Card className="border-border/60 p-6">
        <h2 className="font-heading text-lg font-medium mb-4">Profilo</h2>
        <div className="flex items-center gap-4">
          <Avatar className="w-16 h-16">
            {user?.picture ? <AvatarImage src={user.picture} /> : null}
            <AvatarFallback className="bg-primary/10 text-primary font-semibold">{initials}</AvatarFallback>
          </Avatar>
          <div>
            <div className="font-medium">{user?.name || "—"}</div>
            <div className="text-sm text-muted-foreground">{user?.email}</div>
            <div className="text-xs text-muted-foreground mt-1 capitalize">Ruolo: {user?.role} · Autenticazione: {user?.auth_provider}</div>
          </div>
        </div>
      </Card>

      <Card className="border-border/60 p-6">
        <h2 className="font-heading text-lg font-medium mb-4">Aspetto</h2>
        <div className="flex items-center justify-between">
          <div>
            <div className="font-medium">Tema {theme === "dark" ? "scuro" : "chiaro"}</div>
            <p className="text-sm text-muted-foreground mt-0.5">Il tema chiaro è predefinito. Passa allo scuro quando preferisci.</p>
          </div>
          <Button variant="outline" onClick={toggle} className="gap-2" data-testid="settings-theme-toggle">
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            Passa a {theme === "dark" ? "chiaro" : "scuro"}
          </Button>
        </div>
      </Card>

      {user?.role === "admin" && priceCfg && (
        <Card className="border-border/60 p-6" data-testid="unlock-price-card">
          <div className="flex items-center gap-2 mb-4">
            <Lock className="w-4 h-4 text-primary" />
            <h2 className="font-heading text-lg font-medium">Prezzo Sblocco Allegati</h2>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            Prezzo che il cliente paga (una tantum) per accedere al pacchetto documentale completo di un'opportunità.
          </p>
          <div className="grid grid-cols-3 gap-3 mb-4 text-sm">
            <div className="rounded-md border border-border/60 p-3">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Attuale</p>
              <p className="font-heading text-2xl font-semibold tabular-nums mt-1">€ {priceCfg.effective.amount.toFixed(2).replace(".", ",")}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {priceCfg.effective.source === "stripe_catalog" && "Da catalogo Stripe"}
                {priceCfg.effective.source === "global_override" && "Override globale attivo"}
                {priceCfg.effective.source === "property_override" && "Override su questo immobile"}
              </p>
            </div>
            <div className="rounded-md border border-border/60 p-3">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Catalogo Stripe</p>
              <p className="font-heading text-2xl font-semibold tabular-nums mt-1">
                {priceCfg.catalog?.amount != null ? "€ " + priceCfg.catalog.amount.toFixed(2).replace(".", ",") : "—"}
              </p>
              <p className="text-xs text-muted-foreground mt-1">Fallback se non hai override</p>
            </div>
            <div className="rounded-md border border-border/60 p-3">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Override globale</p>
              <p className="font-heading text-2xl font-semibold tabular-nums mt-1">
                {priceCfg.override ? "€ " + priceCfg.override.amount.toFixed(2).replace(".", ",") : "—"}
              </p>
              <p className="text-xs text-muted-foreground mt-1">{priceCfg.override ? "Attivo" : "Non impostato"}</p>
            </div>
          </div>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Label htmlFor="unlock-price-input">Nuovo prezzo globale (€)</Label>
              <Input
                id="unlock-price-input"
                type="number" step="0.01" min="0"
                value={priceInput}
                onChange={(e) => setPriceInput(e.target.value)}
                placeholder="69.00"
                data-testid="unlock-price-input"
              />
            </div>
            <Button
              onClick={async () => {
                setPriceBusy(true);
                try {
                  await api.put("/admin/unlock-price", { amount: Number(priceInput), currency: "eur" });
                  const { data } = await api.get("/admin/unlock-price");
                  setPriceCfg(data); setPriceInput(String(data.effective.amount));
                  toast.success("Prezzo aggiornato");
                } catch (e) { toast.error(formatApiError(e)); }
                finally { setPriceBusy(false); }
              }}
              disabled={priceBusy || !priceInput || Number(priceInput) < 0}
              data-testid="save-unlock-price"
            >
              {priceBusy ? "Salvataggio…" : "Salva prezzo"}
            </Button>
            {priceCfg.override && (
              <Button
                variant="outline"
                onClick={async () => {
                  if (!window.confirm("Rimuovere l'override globale? Torneremo al prezzo del catalogo Stripe.")) return;
                  setPriceBusy(true);
                  try {
                    await api.delete("/admin/unlock-price");
                    const { data } = await api.get("/admin/unlock-price");
                    setPriceCfg(data); setPriceInput(String(data.effective.amount));
                    toast.success("Override rimosso");
                  } catch (e) { toast.error(formatApiError(e)); }
                  finally { setPriceBusy(false); }
                }}
                disabled={priceBusy}
                data-testid="clear-unlock-price"
              >
                Ripristina catalogo
              </Button>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            💡 Puoi anche impostare un prezzo diverso per un singolo immobile dalla sua scheda — quello ha priorità su questo globale.
          </p>
        </Card>
      )}


      {cfg && (
        <Card className="border-border/60 p-6" data-testid="score-config-card">
          <div className="flex items-center gap-2 mb-4">
            <Gauge className="w-4 h-4 text-primary" />
            <h2 className="font-heading text-lg font-medium">Opportunity Score</h2>
          </div>
          <p className="text-sm text-muted-foreground mb-4">Regole configurabili (0-100). Il punteggio finale è la somma pesata dei fattori. Somma pesi corrente: <span className={`font-medium tabular-nums ${totalW === 100 ? "text-emerald-600" : "text-amber-600"}`}>{totalW}</span></p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(cfg.weights).map(([k, v]) => (
              <div key={k}>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground capitalize">{k}</Label>
                <Input type="number" min="0" max="100" value={v} disabled={!isAdmin}
                  onChange={(e) => setCfg({ ...cfg, weights: { ...cfg.weights, [k]: e.target.value } })}
                  data-testid={`weight-${k}`}
                />
              </div>
            ))}
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Target margine %</Label>
              <Input type="number" step="any" value={cfg.target_margin_pct} disabled={!isAdmin}
                onChange={(e) => setCfg({ ...cfg, target_margin_pct: e.target.value })}
                data-testid="target-margin-pct"
              />
              <p className="text-xs text-muted-foreground mt-1">Margine/prezzo necessario per punteggio pieno.</p>
            </div>
          </div>
          <div className="mt-6">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Punti per stato pratica</Label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
              {Object.entries(cfg.dossier_points).map(([k, v]) => (
                <div key={k}>
                  <Label className="text-xs capitalize">{k.replace("_", " ")}</Label>
                  <Input type="number" min="0" max="100" value={v} disabled={!isAdmin}
                    onChange={(e) => setCfg({ ...cfg, dossier_points: { ...cfg.dossier_points, [k]: e.target.value } })}
                    data-testid={`dossier-${k}`}
                  />
                </div>
              ))}
            </div>
          </div>
          {isAdmin && (
            <div className="mt-6 flex justify-end">
              <Button onClick={saveCfg} disabled={busy} data-testid="save-score-config">{busy ? "Salvataggio…" : "Salva configurazione"}</Button>
            </div>
          )}
          {!isAdmin && <p className="text-xs text-muted-foreground mt-4">Solo gli amministratori possono modificare questa configurazione.</p>}
        </Card>
      )}

      {templates && (
        <Card className="border-border/60 p-6" data-testid="doc-templates-card">
          <div className="flex items-center gap-2 mb-1">
            <FileCheck className="w-4 h-4 text-primary" />
            <h2 className="font-heading text-lg font-medium">Template documenti per tipologia</h2>
          </div>
          <p className="text-sm text-muted-foreground mb-4">Documenti richiesti di default in base al tipo di immobile. Un documento per riga.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(templates).map(([type, list]) => (
              <div key={type}>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground capitalize">{type}</Label>
                <textarea
                  className="mt-1 w-full h-40 p-2 text-sm rounded-md border border-input bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  value={(list || []).join("\n")}
                  disabled={!isAdmin}
                  onChange={(e) => setTemplates({ ...templates, [type]: e.target.value.split("\n").map((s) => s).filter((_, i, arr) => true) })}
                  onBlur={(e) => setTemplates({ ...templates, [type]: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })}
                  data-testid={`tpl-${type}`}
                />
              </div>
            ))}
          </div>
          {isAdmin && (
            <div className="mt-4 flex justify-end">
              <Button onClick={async () => {
                setTplBusy(true);
                try {
                  const cleaned = Object.fromEntries(Object.entries(templates).map(([k, v]) => [k, (v || []).map((s) => (s || "").trim()).filter(Boolean)]));
                  const { data } = await api.put("/settings/doc-templates", { templates: cleaned });
                  setTemplates(data.templates); toast.success("Template salvati");
                } catch (e) { toast.error(formatApiError(e)); }
                finally { setTplBusy(false); }
              }} disabled={tplBusy} data-testid="save-doc-templates">{tplBusy ? "Salvataggio…" : "Salva template"}</Button>
            </div>
          )}
        </Card>
      )}

      <Card className="border-border/60 p-6">
        <h2 className="font-heading text-lg font-medium mb-4">Sessione</h2>
        <Button variant="destructive" onClick={logout} data-testid="settings-logout">Esci dall'account</Button>
      </Card>
    </div>
  );
}